import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, CreditCard, Bell, Smartphone, Landmark, Plus, X, BellRing, BellOff, Send } from "lucide-react";
import usePushNotifications from "@/hooks/usePushNotifications";
import { PrivacySection } from "@/components/PrivacySection";
import { FiscalDataSection } from "@/components/FiscalDataSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TAX_OPTIONS = [
  { id: "none", label: "Sin impuesto", desc: "0% — no aplico impuestos" },
  { id: "iva", label: "IVA", desc: "21% — Península y Baleares" },
  { id: "igic", label: "IGIC", desc: "7% — Canarias" },
];

const COLOR_PRESETS = ["#EA580C", "#2563EB", "#16A34A", "#9333EA", "#0891B2", "#DC2626", "#0F172A", "#CA8A04"];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    business_name: user?.business_name || "",
    phone: user?.phone || "",
    default_tax_type: user?.default_tax_type || "iva",
    brand_color: user?.brand_color || "#EA580C",
    iban: user?.iban || "",
  });

  const save = async (e) => {
    e.preventDefault();
    await api.put("/profile", form);
    await refresh();
    toast.success("Perfil actualizado");
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await api.post("/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await refresh();
    await loadLogoStatus();
    toast.success("Logo subido");
  };

  const [logoStatus, setLogoStatus] = useState(null);

  const loadLogoStatus = async () => {
    try {
      const { data } = await api.get("/profile/logo/status");
      setLogoStatus(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadLogoStatus(); /* eslint-disable-next-line */ }, [user?.logo_url]);

  const restoreLogo = async () => {
    try {
      const { data } = await api.post("/profile/logo/restore");
      if (data?.restored) toast.success("Logo restaurado desde la base de datos");
      else toast.info("El logo ya estaba disponible");
      await loadLogoStatus();
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo restaurar — vuelve a subirlo");
    }
  };

  const logoSrc = user?.logo_url ? `${BACKEND}${user.logo_url}` : null;

  return (
    <div data-testid="profile-page" className="max-w-3xl">
      <div className="kbar">Mi perfil</div>
      <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Datos del negocio</h1>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        <div className="bg-white p-6 sm:col-span-1">
          <div className="kbar mb-3">Logo</div>
          {logoSrc ? (
            <img src={logoSrc} alt="logo" className="w-32 h-32 object-contain border border-slate-200 bg-white" data-testid="profile-logo-img" />
          ) : (
            <div className="w-32 h-32 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300">Sin logo</div>
          )}
          <label className="mt-4 inline-flex items-center gap-2 h-11 px-4 border border-slate-200 hover:border-slate-300 cursor-pointer text-sm font-semibold" data-testid="upload-logo-btn">
            <Upload className="w-4 h-4" />
            Subir logo
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadLogo} />
          </label>
          <p className="text-xs text-slate-400 mt-2">PNG/JPG. Aparecerá en tus PDFs.</p>

          {logoStatus && logoStatus.status !== "none" && (
            <div className="mt-3 text-xs" data-testid="logo-status">
              {logoStatus.status === "ok" && (
                <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Persistente en base de datos
                </span>
              )}
              {logoStatus.status === "recoverable" && (
                <div className="space-y-2">
                  <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">
                    Logo no presente en disco — recuperable
                  </span>
                  <button type="button" onClick={restoreLogo} className="block h-9 px-3 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-xs font-semibold" data-testid="restore-logo-btn">
                    Restaurar logo desde BD
                  </button>
                </div>
              )}
              {logoStatus.status === "needs_reupload" && (
                <span className="inline-flex items-center gap-1.5 text-red-700 bg-red-50 border border-red-200 px-2 py-1">
                  Logo perdido — vuélvelo a subir
                </span>
              )}
            </div>
          )}
        </div>

        <form onSubmit={save} className="bg-white p-6 sm:col-span-2 space-y-4">
          <div>
            <label className="kbar block">Nombre completo</label>
            <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600" data-testid="profile-name" />
          </div>
          <div>
            <label className="kbar block">Nombre del negocio</label>
            <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600" data-testid="profile-business" />
          </div>
          <div>
            <label className="kbar block">Teléfono</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600" data-testid="profile-phone" />
          </div>
          <div>
            <label className="kbar block">Email</label>
            <input value={user?.email || ""} disabled className="w-full h-12 mt-2 px-3 border border-slate-200 bg-slate-50 text-slate-500" />
          </div>

          <div>
            <label className="kbar block">IBAN / Cuenta corriente</label>
            <p className="text-xs text-slate-500 mb-2">Aparecerá en el pie de cada presupuesto y factura para que el cliente sepa dónde pagar.</p>
            <input
              data-testid="profile-iban"
              value={form.iban}
              onChange={(e) => setForm({ ...form, iban: e.target.value })}
              placeholder="ES00 0000 0000 0000 0000 0000"
              className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600 font-mono"
            />
          </div>

          <div>
            <label className="kbar block mb-2">Gravamen predeterminado</label>
            <p className="text-xs text-slate-500 mb-3">Se aplicará automáticamente a los presupuestos nuevos. Podrás cambiarlo en cada uno.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {TAX_OPTIONS.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  data-testid={`profile-tax-${t.id}`}
                  onClick={() => setForm({ ...form, default_tax_type: t.id })}
                  className={`p-3 text-left border transition-colors ${form.default_tax_type === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}
                >
                  <div className="font-bold text-sm">{t.label}</div>
                  <div className={`text-xs ${form.default_tax_type === t.id ? "text-slate-300" : "text-slate-500"}`}>{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="kbar block mb-2">Color corporativo</label>
            <p className="text-xs text-slate-500 mb-3">Se usa en PDFs y emails enviados al cliente.</p>
            <div className="grid grid-cols-8 gap-2">
              {COLOR_PRESETS.map((c) => (
                <button key={c} type="button" data-testid={`profile-color-${c.replace("#","")}`}
                  onClick={() => setForm({ ...form, brand_color: c })}
                  className={`h-10 transition-all ${form.brand_color === c ? "ring-2 ring-offset-2 ring-slate-300" : "hover:scale-105"}`}
                  style={{ background: c }} />
              ))}
            </div>
            <div className="flex items-center gap-3 mt-2">
              <input data-testid="profile-color-custom" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10 px-3 border border-slate-200 outline-none focus:border-orange-600 w-32 font-mono text-sm" />
              <div className="h-10 w-10 border border-slate-200" style={{ background: form.brand_color }} />
            </div>
          </div>

          <button data-testid="profile-save" className="btn-brand h-12 px-6">Guardar cambios</button>
        </form>
      </div>

      <FiscalDataSection />
      <AccountingSection />
      <BudgetDefaultsSection />
      <PaymentMethodsSection />
      <ReminderSettingsSection />
      <PushNotificationsSection />
      <PrivacySection />
    </div>
  );
}

function AccountingSection() {
  const [cfg, setCfg] = useState({ provider: "none", connected: false });
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/profile/accounting").then((r) => setCfg(r.data)); }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/profile/accounting", { provider: cfg.provider, api_key: apiKey || undefined });
      toast.success("Conexión guardada");
      setApiKey("");
      const r = await api.get("/profile/accounting");
      setCfg(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-px border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-3 mb-2">
        <FileText className="w-5 h-5 text-slate-400" />
        <h2 className="font-display font-bold text-xl">Conectar contabilidad</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">Convierte tus presupuestos aceptados en facturas con un clic. Tus datos se quedan en tu cuenta del proveedor.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          { id: "none", label: "Sin conectar", desc: "Modo manual" },
          { id: "holded", label: "Holded", desc: "holded.com" },
          { id: "quipu", label: "Quipu", desc: "getquipu.com" },
        ].map((p) => (
          <button key={p.id} type="button" data-testid={`acc-${p.id}`} onClick={() => setCfg({ ...cfg, provider: p.id })}
            className={`p-3 text-left border transition-colors ${cfg.provider === p.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}>
            <div className="font-bold text-sm">{p.label}</div>
            <div className={`text-xs ${cfg.provider === p.id ? "text-slate-300" : "text-slate-500"}`}>{p.desc}</div>
          </button>
        ))}
      </div>

      {cfg.provider !== "none" && (
        <div className="mt-4 space-y-3">
          <div>
            <label className="kbar block">API Key de {cfg.provider === "holded" ? "Holded" : "Quipu"}</label>
            <input
              data-testid="acc-api-key" type="password"
              value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg.connected ? "•••••••••• (clave guardada — sobrescribe para cambiar)" : "Pega aquí tu API key"}
              className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none font-mono"
            />
            <p className="text-xs text-slate-400 mt-2">
              {cfg.provider === "holded"
                ? "Obtén tu key en Configuración → Desarrolladores → API en Holded"
                : "Obtén tu token en Configuración → API en Quipu"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={loading} data-testid="acc-save" className="btn-brand h-11 px-5">{loading ? "Guardando…" : "Guardar conexión"}</button>
            {cfg.connected && <span className="inline-flex items-center gap-1 text-sm text-green-700"><CheckCircle2 className="w-4 h-4" /> Conectado</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function BudgetDefaultsSection() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    default_terms: user?.default_terms || "",
    default_delivery_days: user?.default_delivery_days ?? "",
    default_validity_days: user?.default_validity_days ?? "",
    default_warranty: user?.default_warranty || "",
    default_payment_method: user?.default_payment_method || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        default_terms: form.default_terms || "",
        default_delivery_days: form.default_delivery_days === "" ? null : parseInt(form.default_delivery_days),
        default_validity_days: form.default_validity_days === "" ? null : parseInt(form.default_validity_days),
        default_warranty: form.default_warranty || "",
        default_payment_method: form.default_payment_method || "",
      };
      await api.put("/profile", payload);
      await refresh();
      toast.success("Plantilla guardada — se aplicará a tus próximos presupuestos");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-px border border-slate-200 bg-white p-6" data-testid="budget-defaults-section">
      <div className="flex items-center gap-3 mb-2">
        <FileText className="w-5 h-5 text-slate-400" />
        <h2 className="font-display font-bold text-xl">Plantilla por defecto para presupuestos</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Estos valores se aplican automáticamente al crear un presupuesto nuevo, así no los reescribes cada vez.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="kbar">Días de entrega</label>
          <input
            data-testid="defaults-delivery"
            type="number"
            min="0"
            max="365"
            value={form.default_delivery_days}
            onChange={(e) => setForm({ ...form, default_delivery_days: e.target.value })}
            placeholder="ej. 7"
            className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600"
          />
        </div>
        <div>
          <label className="kbar">Validez del presupuesto (días)</label>
          <input
            data-testid="defaults-validity"
            type="number"
            min="1"
            max="365"
            value={form.default_validity_days}
            onChange={(e) => setForm({ ...form, default_validity_days: e.target.value })}
            placeholder="ej. 30"
            className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="kbar">Garantía</label>
          <input
            data-testid="defaults-warranty"
            value={form.default_warranty}
            onChange={(e) => setForm({ ...form, default_warranty: e.target.value })}
            placeholder="ej. 12 meses sobre mano de obra y materiales"
            className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="kbar">Forma de pago</label>
          <input
            data-testid="defaults-payment"
            value={form.default_payment_method}
            onChange={(e) => setForm({ ...form, default_payment_method: e.target.value })}
            placeholder="ej. 50% al inicio, 50% al finalizar · transferencia o Bizum"
            className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="kbar">Términos y condiciones</label>
          <textarea
            data-testid="defaults-terms"
            value={form.default_terms}
            onChange={(e) => setForm({ ...form, default_terms: e.target.value })}
            placeholder="Pago: 50% al inicio, 50% al finalizar. Garantía 12 meses. Trabajos no incluidos en este presupuesto serán facturados aparte previa autorización…"
            rows={6}
            className="w-full mt-2 px-3 py-2 border border-slate-200 outline-none focus:border-orange-600 text-sm"
          />
          <p className="text-xs text-slate-500 mt-1">Aparece en la sección final del PDF de cada presupuesto.</p>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="defaults-save" className="btn-brand h-11 px-5 mt-4">
        {saving ? "Guardando…" : "Guardar plantilla"}
      </button>
    </div>
  );
}

function PaymentMethodsSection() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    bizum_phone: user?.bizum_phone || "",
    stripe_payment_link: user?.stripe_payment_link || "",
    payment_note: user?.payment_note || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      await refresh();
      toast.success("Métodos de cobro actualizados");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-px border border-slate-200 bg-white p-6" data-testid="payment-methods-section">
      <div className="flex items-center gap-3 mb-2">
        <CreditCard className="w-5 h-5 text-slate-400" />
        <h2 className="font-display font-bold text-xl">Métodos de cobro</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">Aparecerán en la página pública de cada factura. Deja en blanco los que no quieras ofrecer.</p>

      <div className="space-y-4">
        <div>
          <label className="kbar flex items-center gap-2"><Smartphone className="w-3 h-3" /> Bizum</label>
          <input
            data-testid="profile-bizum"
            value={form.bizum_phone}
            onChange={(e) => setForm({ ...form, bizum_phone: e.target.value })}
            placeholder="+34 600 00 00 00"
            className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600 font-mono"
          />
        </div>
        <div>
          <label className="kbar flex items-center gap-2"><CreditCard className="w-3 h-3" /> Enlace de pago Stripe</label>
          <p className="text-xs text-slate-500 mt-0.5">Créalo en <a href="https://dashboard.stripe.com/payment-links" target="_blank" rel="noreferrer" className="text-orange-600 underline">Stripe → Payment Links</a>. Pégalo aquí.</p>
          <input
            data-testid="profile-stripe-link"
            value={form.stripe_payment_link}
            onChange={(e) => setForm({ ...form, stripe_payment_link: e.target.value })}
            placeholder="https://buy.stripe.com/..."
            className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600 font-mono text-xs"
          />
        </div>
        <div>
          <label className="kbar">Nota de pago</label>
          <p className="text-xs text-slate-500 mt-0.5">Texto corto que verá el cliente (ej. "Por favor, indica el número de factura").</p>
          <input
            data-testid="profile-payment-note"
            value={form.payment_note}
            onChange={(e) => setForm({ ...form, payment_note: e.target.value })}
            placeholder="p.ej. Indica el número de factura en el concepto"
            className="w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600"
          />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="payment-methods-save" className="btn-brand h-11 px-5 mt-4">
        {saving ? "Guardando…" : "Guardar métodos de cobro"}
      </button>
    </div>
  );
}

function ReminderSettingsSection() {
  const [settings, setSettings] = useState({ enabled: true, days_after_due: [3, 7, 15], template: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDay, setNewDay] = useState(3);

  useEffect(() => {
    api.get("/profile/reminders").then((r) => {
      setSettings({ enabled: r.data.enabled, days_after_due: r.data.days_after_due || [], template: r.data.template || "" });
      setLoading(false);
    });
  }, []);

  const addDay = () => {
    const d = Number(newDay);
    if (!d || d < 1 || d > 365) return toast.error("Día debe ser entre 1 y 365");
    if (settings.days_after_due.includes(d)) return toast.error("Ese día ya existe");
    setSettings({ ...settings, days_after_due: [...settings.days_after_due, d].sort((a, b) => a - b) });
  };
  const removeDay = (d) => setSettings({ ...settings, days_after_due: settings.days_after_due.filter((x) => x !== d) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/reminders", settings);
      toast.success("Recordatorios actualizados");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="mt-px border border-slate-200 bg-white p-6" data-testid="reminder-settings-section">
      <div className="flex items-center gap-3 mb-2">
        <Bell className="w-5 h-5 text-slate-400" />
        <h2 className="font-display font-bold text-xl">Recordatorios automáticos de cobro</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">Enviamos un email al cliente cuando la factura lleva N días vencida y sigue sin cobrarse.</p>

      <div className="flex items-center gap-3 mb-4">
        <button
          data-testid="reminders-toggle"
          role="switch"
          aria-checked={settings.enabled}
          onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
          className={`h-11 px-5 font-bold text-sm inline-flex items-center gap-2 border transition-colors ${settings.enabled ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
        >
          <span className={`w-2 h-2 rounded-full ${settings.enabled ? "bg-green-400" : "bg-slate-400"}`} />
          {settings.enabled ? "Activados" : "Desactivados"}
        </button>
      </div>

      {settings.enabled && (
        <>
          <div>
            <label className="kbar block mb-2">Enviar recordatorio a los X días de vencimiento</label>
            <div className="flex flex-wrap gap-2 items-center">
              {settings.days_after_due.map((d) => (
                <span key={d} data-testid={`reminder-day-${d}`} className="inline-flex items-center gap-1 h-9 px-3 bg-slate-100 text-sm font-semibold">
                  {d} días
                  <button onClick={() => removeDay(d)} className="ml-1 text-slate-500 hover:text-red-600"><X className="w-3 h-3" /></button>
                </span>
              ))}
              <div className="inline-flex items-center gap-1">
                <input
                  type="number"
                  data-testid="reminder-new-day"
                  value={newDay}
                  onChange={(e) => setNewDay(e.target.value)}
                  min={1}
                  max={365}
                  className="h-9 w-20 px-2 border border-slate-200 outline-none focus:border-orange-600 text-sm tabular-nums"
                />
                <button onClick={addDay} data-testid="reminder-add-day" className="h-9 w-9 bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <label className="kbar block mb-2">Texto adicional (opcional)</label>
            <p className="text-xs text-slate-500 mb-2">Se añade al email estándar. Útil para un tono personal.</p>
            <textarea
              data-testid="reminder-template"
              rows={3}
              value={settings.template}
              onChange={(e) => setSettings({ ...settings, template: e.target.value })}
              placeholder="Hola, ¿te acordaste del pago de la factura?"
              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-orange-600 resize-none text-sm"
            />
          </div>
        </>
      )}

      <button onClick={save} disabled={saving} data-testid="reminders-save" className="btn-brand h-11 px-5 mt-4">
        {saving ? "Guardando…" : "Guardar recordatorios"}
      </button>
    </div>
  );
}


function PushNotificationsSection() {
  const { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest } = usePushNotifications();

  return (
    <div className="mt-px border border-slate-200 bg-white p-6" data-testid="push-notifications-section">
      <div className="flex items-center gap-3 mb-2">
        <BellRing className="w-5 h-5 text-slate-400" />
        <h2 className="font-display font-bold text-xl">Notificaciones push</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Recibe avisos en tu móvil u ordenador cuando un cliente vea, acepte o firme tu presupuesto — incluso con la app cerrada.
      </p>

      {!supported ? (
        <div className="bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-900">
          Tu navegador no soporta notificaciones push. Prueba con Chrome, Firefox o Safari 16.4+.
        </div>
      ) : permission === "denied" ? (
        <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-900">
          Bloqueaste las notificaciones. Actívalas desde los ajustes del navegador (icono del candado junto a la URL) y recarga.
        </div>
      ) : subscribed ? (
        <div className="space-y-3">
          <div className="bg-green-50 border border-green-200 p-3 text-sm text-green-800 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Notificaciones activas en este dispositivo
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={sendTest} disabled={loading} data-testid="push-test-btn" className="h-11 px-4 border border-slate-200 hover:border-slate-300 text-sm font-bold inline-flex items-center gap-2">
              <Send className="w-4 h-4" /> Enviar notificación de prueba
            </button>
            <button onClick={unsubscribe} disabled={loading} data-testid="push-unsubscribe-btn" className="h-11 px-4 border border-slate-200 hover:border-red-400 hover:text-red-600 text-sm font-bold inline-flex items-center gap-2">
              <BellOff className="w-4 h-4" /> Desactivar en este dispositivo
            </button>
          </div>
        </div>
      ) : (
        <button onClick={subscribe} disabled={loading} data-testid="push-subscribe-btn" className="btn-brand h-12 px-6 inline-flex items-center gap-2">
          {loading ? "Activando…" : <><BellRing className="w-5 h-5" /> Activar notificaciones</>}
        </button>
      )}

      <details className="mt-5 text-sm text-slate-500">
        <summary className="cursor-pointer font-semibold hover:text-slate-700">¿Qué notificaciones recibiré?</summary>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>👀 Cuando un cliente vea por primera vez tu presupuesto</li>
          <li>🎉 Cuando un cliente acepte un presupuesto</li>
          <li>✍️ Cuando un cliente firme digitalmente</li>
          <li>💰 Cuando un cliente notifique que ha pagado una factura</li>
        </ul>
      </details>
    </div>
  );
}

